import api, { refreshAccessToken } from './client'

export type NodeType = 'pump' | 'reservoir' | 'flow_meter' | 'pressure_sensor' | 'level_sensor' | 'valve'
export type ZoneCategory = 'well' | 'reservoir' | 'distribution' | 'source'
export type ReservoirType = 'filtering' | 'accumulation' | 'divergence'
export type DataQuality = 'ok' | 'stale' | 'estimated' | 'inferred'

// CHECK-enforced set on scada_devices.comm_error (migration 038) —
// pymodbus's own exception names, plus no_response (unit_id timed out
// with no reply) and unknown (a genuine non-protocol failure). Exhaustive,
// not illustrative — see docs/plan-modbus-comm-error-visibility.md.
export type DeviceCommErrorCategory =
  | 'IllegalFunction' | 'IllegalAddress' | 'IllegalValue' | 'SlaveFailure'
  | 'Acknowledge' | 'SlaveBusy' | 'NegativeAcknowledge' | 'MemoryParityError'
  | 'GatewayPathUnavailable' | 'GatewayNoResponse' | 'no_response' | 'unknown'

// Backend-decided live status (services/alerts.py's device_statuses) -- one
// answer for every client, instead of each re-deriving staleness/severity
// from raw readings. 'no_signal' + severity 'unknown' is the gray blackout
// state; a stopped pump is 'stopped' + 'ok' (normal) or + 'warning'/
// 'critical' (an open alert episode), never gray.
export type DeviceStatus = 'running' | 'stopped' | 'reporting' | 'no_signal' | 'inactive'
export type StatusSeverity = 'critical' | 'warning' | 'unknown' | 'ok'

export interface DeviceStatusFields {
  status:          DeviceStatus
  status_severity: StatusSeverity | null // null only for inactive devices
  last_seen_at:    string | null         // last reading on any channel, extra readings included
  // Pumps only: the kvar fault bit and when its current run began. Render only
  // on `fault === true`; null/undefined/false render nothing.
  fault?:          boolean | null
  fault_since?:    string | null
}

export interface PumpStatus extends DeviceStatusFields {
  device_key:   string
  label:        string
  active:       boolean | null
  flow_lps:     number | null
  pressure_kpa: number | null
  recorded_at:  string | null
  // A separate fact from the reading above — both can be non-null at once
  // (a good last reading from 10 minutes ago, a fresh error from the most
  // recent failed poll). Never lets one hide the other in the UI.
  comm_error:        DeviceCommErrorCategory | null
  comm_error_detail: string | null
  comm_error_at:     string | null
  location: { lat: number | null; lng: number | null }
}

export interface ReservoirStatus extends DeviceStatusFields {
  device_key:             string
  label:                  string
  depth_m:                number | null
  volume_m3:              number | null
  max_depth_m:            number | null
  capacity_m3:            number | null
  warning_depth_m:        number | null
  critical_depth_m:       number | null
  rate_m_per_h:           number | null        // negative = draining
  minutes_to_critical:    number | null
  time_to_empty_minutes:  number | null        // volume-based TTE
  tte_alert_threshold:    number | null
  recorded_at:            string | null
  comm_error:        DeviceCommErrorCategory | null
  comm_error_detail: string | null
  comm_error_at:     string | null
  location: { lat: number | null; lng: number | null }
}

export interface ScadaLatest {
  pumps:      PumpStatus[]
  reservoirs: ReservoirStatus[]
}

export interface PumpHistoryPoint {
  active:       boolean
  flow_lps:     number | null
  pressure_kpa: number | null
  recorded_at:  string
}

export interface ReservoirHistoryPoint {
  depth_m:    number
  volume_m3:  number | null
  recorded_at: string
}

// SSE event shapes
export type ScadaEvent =
  | { type: 'pump';      device_key: string; active: boolean;  flow_lps: number | null; recorded_at: string }
  | { type: 'reservoir'; device_key: string; depth_m: number;  volume_m3: number | null; recorded_at: string }
  | { type: 'flow';      device_key: string; flow_lps: number; recorded_at: string }

export type ModbusRegisterType = 'holding_register' | 'coil' | 'input_register' | 'discrete_input'

export interface ModbusReading {
  field:       string
  type:        ModbusRegisterType
  address:     number
  scale?:      number
  // Groups readings for evaluate_after_extra_reading's same-type divergence
  // check (backend/app/services/alerts.py) — deliberately open-ended, no
  // fixed enum to keep in sync with. undefined = plain passthrough.
  sensor_type?: string
}

export interface ScadaDevice extends DeviceStatusFields {
  id:                          string
  device_key:                  string
  node_type:                   NodeType
  label:                       string
  active:                      boolean
  zone_id:                     string  // owner zone, required (device-ownership model, migration 055)
  access_point_id:             string | null
  location_lat:                number | null
  location_lng:                number | null
  reservoir_type:              ReservoirType | null
  max_depth_m:                 number | null
  capacity_m3:                 number | null
  warning_depth_m:             number | null
  critical_depth_m:            number | null
  floater_shutoff_depth_m:     number | null
  floater_activation_depth_m:  number | null
  alert_offline_minutes:       number | null // 'no signal' threshold, every node type
  alert_stopped_minutes:       number | null // pumps only: stopped this long -> alert
  alert_low_depth_m:           number | null // legacy; reservoir depth now uses alert_low/warn_low
  alert_time_to_empty_minutes: number | null
  expected_value:              number | null
  warn_low:                    number | null
  warn_high:                   number | null
  alert_low:                   number | null
  alert_high:                  number | null
  measurement_unit:            string | null
  poll_interval_seconds:       number | null
  deadband:                    number | null
  heartbeat_seconds:           number | null
  modbus_unit_id:              number | null
  modbus_address_base:         0 | 1
  modbus_readings:             ModbusReading[]
  alerts_muted:                boolean
  alerts_muted_at:             string | null
  // GET /scada/devices only (list endpoints -- flow_readings/extra_readings
  // aren't joined elsewhere) -- flow_meter's own flow_lps, everything else's
  // newest extra_readings row regardless of field. severity is derived
  // backend-side from warn_low/warn_high/alert_low/alert_high, the same
  // priority rule evaluate_extra_reading_threshold uses on write. For
  // pump/reservoir this is a *different* signal than pumpPulse/reservoirPulse
  // (which read pump_readings/reservoir_readings) -- additive coverage for
  // the four device types that had none, not a replacement.
  latest_value:                number | null
  latest_recorded_at:          string | null
  severity:                    'warning' | 'critical' | null
}

export interface ScadaBoundaryPoint {
  lat:   number
  lng:   number
  order: number
}

export interface ScadaZone {
  id:               string
  label:            string
  description:      string | null
  zone_category:    ZoneCategory
  active:           boolean
  boundary_polygon: ScadaBoundaryPoint[]
  created_at:       string
}

// access_point_type has no enum server-side yet — 'manhole' is the only
// value in use today, but the field is a plain string, not a closed set.
export interface ScadaAccessPoint {
  id:                string
  access_point_key:  string
  label:             string
  access_point_type: string
  description:       string | null
  // Owner zone -- required for new/edited rows at the API (not yet a DB
  // NOT NULL like scada_devices.zone_id, so an un-migrated legacy row can
  // still read back null).
  zone_id:           string | null
  // Deprecated, device-ownership step 1: never accepted or written; a
  // response always carries it as null. Pipe attachment is a path point.
  zone_link_id:      null
  location_lat:      number | null
  location_lng:      number | null
  active:            boolean
  created_at:        string
}

export type ZoneFlowStatus = 'deficit' | 'balanced' | 'surplus' | 'unknown'

export interface ZoneStatus {
  zone_id:           string
  label:             string
  status:            ZoneFlowStatus
  // deficit=critical, surplus=warning, balanced=ok, unknown=unknown --
  // the same severity the zone push carries.
  severity:          StatusSeverity
  net_flow_lps:      number | null
  nominal_flow_lps:  number | null
}

export type PathPointType = 'bend' | 'device' | 'access_point'

export interface ScadaZoneLinkPoint {
  // Real scada_zone_link_points row id (migration 045) — present on every
  // GET response, not required/read on a PATCH .../path submission (still
  // full-replace; the backend regenerates ids). Optional so a freshly
  // client-added point (no id yet) still type-checks.
  id?:              string
  // Nullable for a device/access_point point (device-ownership step 2, one
  // location per device): reads take the referenced object's own location,
  // which can be unset. Never null for a bend, CHECK-enforced server-side.
  lat:              number | null
  lng:              number | null
  recorded_at:      string
  order:            number
  point_type:       PathPointType
  device_id:        string | null
  access_point_id:  string | null
}

export interface ScadaZoneLink {
  id:                 string
  label:              string
  from_zone_id:       string | null
  to_zone_id:         string | null
  flow_meter_node_id: string | null
  nominal_flow_lps:   number | null
  alert_loss_lps:     number | null
  alert_loss_pct:     number | null
  active:             boolean
  path:               ScadaZoneLinkPoint[]
  created_at:         string
}

export interface PumpReservoirLink {
  id:                     string
  pump_node_id:           string
  reservoir_node_id:      string
  activation_depth_m:     number
  shutoff_depth_m:        number
  startup_delay_minutes:  number
  active:                 boolean
}

export type AlertType =
  | 'pump_offline'          // an unlinked pump stopped past alert_stopped_minutes
  | 'pump_fault'            // kvar fault bit set on a pump
  | 'low_reservoir'
  | 'low_time_to_empty'
  | 'pump_failed_to_start'
  | 'device_offline'        // no signal past alert_offline_minutes
  | 'edge_offline'
  | 'sensor_divergence'
  | 'device_threshold_breach'
  | 'zone_flow_imbalance'
  | 'recovered'

// One row per alert episode (plus a 'recovered' row when one ends). label and
// title are stored/computed backend-side -- the same text the push carried --
// so clients don't keep their own type->text maps.
export interface AlertEvent {
  id:             string
  device_key:     string
  alert_type:     string
  subject:        string | null
  label:          string | null
  title:          string
  detail:         Record<string, unknown>
  severity:       StatusSeverity | null
  fired_at:       string
  last_seen_at:   string | null
  cleared_at:     string | null
  resolved_at:    string | null
  reminders_sent: number
  notified:       number // pushes across the whole episode
}

// GET /devices/{device_key}/extra-readings — generic passthrough for any
// Modbus reading beyond a device's node_type-fixed native field(s). Flat
// shape, no node_type-awareness: render whatever comes back.
export interface ExtraReading {
  field:       string
  sensor_type: string | null
  value:       number
  recorded_at: string
}

export type MasterConnErrorCategory = 'unreachable' | 'refused' | 'reset' | 'unknown'

// Read-only summary of public.edge_nodes — enough for status display
// (ScadaView's status bar, the dashboard's Edge Servers panel). Full
// management (create/modbus config/regenerate key/delete) stays in
// SettingsPage.tsx's own richer EdgeNode type; this one intentionally
// doesn't carry ingress_key or the modbus fields those don't need.
export interface EdgeNodeSummary {
  id:           string
  name:         string
  active:       boolean
  last_seen_at: string | null
  master_conn_error:        MasterConnErrorCategory | null
  master_conn_error_detail: string | null
  master_conn_error_at:     string | null
}

// scada-to-web-2026-09-02-production.md: integrated daily production
// volume (liters produced that day / 1000), not an average flow rate --
// deliberate given pump_readings/flow_readings are sparse and irregular.
// `days` 1–366; every requested day is present even with zero readings
// (volume_m3: 0), oldest first, no gaps for the chart to paper over.
export interface ProductionPoint {
  date:      string  // YYYY-MM-DD
  volume_m3: number
}

export const scadaApi = {
  listEdgeNodes: () => api.get<EdgeNodeSummary[]>('/setup/edge-nodes'),
  latest: () => api.get<ScadaLatest>('/scada/latest'),
  pumpHistory: (deviceKey: string, hours = 6) =>
    api.get<PumpHistoryPoint[]>(`/scada/history/pump/${deviceKey}`, { params: { hours } }),
  reservoirHistory: (deviceKey: string, hours = 6) =>
    api.get<ReservoirHistoryPoint[]>(`/scada/history/reservoir/${deviceKey}`, { params: { hours } }),
  listDevices: () => api.get<ScadaDevice[]>('/scada/devices'),
  createDevice: (body: Omit<ScadaDevice, 'id' | 'active' | 'device_key'>) =>
    api.post<ScadaDevice>('/scada/devices', body),
  updateDevice: (deviceKey: string, body: Partial<ScadaDevice>) =>
    api.patch<ScadaDevice>(`/scada/devices/${deviceKey}`, body),
  deleteDevice: (deviceKey: string) => api.delete(`/scada/devices/${deviceKey}`),
  getExtraReadings: (deviceKey: string) => api.get<ExtraReading[]>(`/scada/devices/${deviceKey}/extra-readings`),
  simulateAlert: (deviceKey: string, alertType: AlertType, depthM?: number) =>
    api.post('/scada/simulate-alert', { device_key: deviceKey, alert_type: alertType, depth_m: depthM }),
  listAlertEvents: (params: { device_key?: string; alert_type?: string; since?: string; until?: string; open?: boolean; limit?: number }) =>
    api.get<AlertEvent[]>('/scada/alert-events', { params }),
  production: (days = 30) => api.get<ProductionPoint[]>('/scada/production', { params: { days } }),

  // Zones
  listZones: () => api.get<ScadaZone[]>('/scada/zones'),
  zoneStatus: () => api.get<ZoneStatus[]>('/scada/zones/status'),
  createZone: (body: Pick<ScadaZone, 'label' | 'zone_category'> & { description?: string }) =>
    api.post<ScadaZone>('/scada/zones', body),
  updateZone: (id: string, body: Partial<Pick<ScadaZone, 'label' | 'zone_category' | 'description' | 'active'>>) =>
    api.patch<ScadaZone>(`/scada/zones/${id}`, body),
  updateZoneBoundary: (id: string, boundary_polygon: ScadaBoundaryPoint[]) =>
    api.patch<ScadaZone>(`/scada/zones/${id}`, { boundary_polygon }),
  deleteZone: (id: string) => api.delete(`/scada/zones/${id}`),

  // Zone links
  listZoneLinks: () => api.get<ScadaZoneLink[]>('/scada/zone-links'),
  createZoneLink: (body: Omit<ScadaZoneLink, 'id' | 'active' | 'created_at'>) =>
    api.post<ScadaZoneLink>('/scada/zone-links', body),
  updateZoneLink: (id: string, body: Partial<Omit<ScadaZoneLink, 'id' | 'created_at' | 'path'>>) =>
    api.patch<ScadaZoneLink>(`/scada/zone-links/${id}`, body),
  updateZoneLinkPath: (id: string, path: ScadaZoneLinkPoint[]) =>
    api.patch<ScadaZoneLink>(`/scada/zone-links/${id}/path`, { path }),
  deleteZoneLink: (id: string) => api.delete(`/scada/zone-links/${id}`),

  // Access points
  listAccessPoints: () => api.get<ScadaAccessPoint[]>('/scada/access-points'),
  createAccessPoint: (body: Omit<ScadaAccessPoint, 'id' | 'access_point_key' | 'created_at' | 'zone_id' | 'zone_link_id'> & { zone_id: string }) =>
    api.post<ScadaAccessPoint>('/scada/access-points', body),
  updateAccessPoint: (id: string, body: Partial<Omit<ScadaAccessPoint, 'id' | 'access_point_key' | 'created_at'>>) =>
    api.patch<ScadaAccessPoint>(`/scada/access-points/${id}`, body),
  deleteAccessPoint: (id: string) => api.delete(`/scada/access-points/${id}`),

  // Pump ↔ reservoir automation (floater thresholds)
  listPumpReservoirLinks: () => api.get<PumpReservoirLink[]>('/scada/pump-reservoir-links'),
  createPumpReservoirLink: (body: Omit<PumpReservoirLink, 'id' | 'active'>) =>
    api.post<PumpReservoirLink>('/scada/pump-reservoir-links', body),
  updatePumpReservoirLink: (id: string, body: Partial<Pick<PumpReservoirLink, 'activation_depth_m' | 'shutoff_depth_m' | 'startup_delay_minutes' | 'active'>>) =>
    api.patch<PumpReservoirLink>(`/scada/pump-reservoir-links/${id}`, body),
  deletePumpReservoirLink: (id: string) => api.delete(`/scada/pump-reservoir-links/${id}`),
}

/** Open SSE stream. Returns cleanup function. */
export function openScadaStream(
  token: string,
  onEvent: (event: ScadaEvent) => void,
  onConnect?: () => void,
): () => void {
  let active = true
  let controller = new AbortController()

  const connect = async () => {
    while (active) {
      try {
        const response = await fetch('/api/v1/scada/stream', {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })

        if (response.status === 401) {
          // Access token expired mid-stream. Refresh through the same
          // shared single-flight path the axios interceptor uses — on
          // success this updates the auth store, and the reactive `token`
          // dependency on the effect that opened this stream tears it
          // down and reopens with the fresh one. On failure,
          // refreshAccessToken() has already forced logout + redirect, so
          // just stop retrying with a token we know is dead.
          try {
            await refreshAccessToken()
          } catch { /* already handled: logout + redirect */ }
          return
        }

        if (!response.ok || !response.body) {
          await new Promise((r) => setTimeout(r, 3000))
          continue
        }

        const reader  = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer    = ''

        onConnect?.()

        while (active) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() ?? ''
          for (const part of parts) {
            const line = part.split('\n').find((l) => l.startsWith('data: '))
            if (!line) continue
            try {
              onEvent(JSON.parse(line.slice(6)) as ScadaEvent)
            } catch { /* skip malformed */ }
          }
        }
      } catch {
        if (!active) break
        await new Promise((r) => setTimeout(r, 3000))
        controller = new AbortController()
      }
    }
  }

  connect()
  return () => { active = false; controller.abort() }
}
